<template>
  <div class="ledger-page">
    <b-card class="p-0 filter-card rounded-lg">
      <b-row>
        <b-col cols="12">
          <h3 class="ml-1 mb-2">
            {{ $t('ledger_tb_title') }}
          </h3>
        </b-col>
        <b-col
          cols="12"
          md="4"
          class="mb-1"
        >
          <b-form-group :label="$t('ledger_as_of')">
            <b-form-input
              v-model="asOf"
              type="date"
            />
          </b-form-group>
          <b-form-checkbox v-model="preClosing">
            {{ $t('ledger_tb_pre_closing') }}
          </b-form-checkbox>
        </b-col>
      </b-row>
    </b-card>

    <b-alert
      v-if="imbalanced"
      show
      variant="danger"
    >
      {{ $t('ledger_tb_imbalance') }}
    </b-alert>

    <b-card class="rounded-lg">
      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!accounts.length"
        class="text-center text-muted py-3"
      >
        {{ $t('ledger_empty_tb') }}
      </div>

      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="accounts"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
            thead-class="ledger-sticky-head"
            tfoot-class="ledger-sticky-foot"
            show-empty
            foot-clone
          >
            <template #cell(code)="data">
              <strong>{{ data.item.code }}</strong>
            </template>
            <template #cell(name)="data">
              {{ accountName(data.item) }}
            </template>
            <template #cell(debit)="data">
              <span class="ledger-num">{{ money(data.item.debit) }}</span>
            </template>
            <template #cell(credit)="data">
              <span class="ledger-num">{{ money(data.item.credit) }}</span>
            </template>
            <template #cell(movement_debit)="data">
              <span class="ledger-num">{{ money(data.item.movement_debit) }}</span>
            </template>
            <template #cell(movement_credit)="data">
              <span class="ledger-num">{{ money(data.item.movement_credit) }}</span>
            </template>
            <template #foot(code)>
              {{ $t('ledger_totals') }}
            </template>
            <template #foot(name)>
              <span />
            </template>
            <template #foot(debit)>
              <strong class="ledger-num">{{ money(totals.debit) }}</strong>
            </template>
            <template #foot(credit)>
              <strong class="ledger-num">{{ money(totals.credit) }}</strong>
            </template>
            <template #foot(movement_debit)>
              <span />
            </template>
            <template #foot(movement_credit)>
              <span />
            </template>
          </b-table>
        </div>

        <div class="d-md-none pb-5">
          <div
            v-for="row in accounts"
            :key="row.gl_account_id || row.code"
            class="ledger-tb-card"
          >
            <div><strong>{{ row.code }}</strong> {{ accountName(row) }}</div>
            <div class="d-flex justify-content-between ledger-num">
              <span>{{ $t('ledger_debit') }} {{ money(row.debit) }}</span>
              <span>{{ $t('ledger_credit') }} {{ money(row.credit) }}</span>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <div class="ledger-tb-totals d-md-none">
      <div class="ledger-num">
        {{ $t('ledger_debit') }} {{ money(totals.debit) }}
      </div>
      <div class="ledger-num">
        {{ $t('ledger_credit') }} {{ money(totals.credit) }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BFormCheckbox, BTable, BAlert, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getTrialBalance } from '@/api/accounting/trialBalance'
import {
  oneFrom, formatLedgerMoney, moneyEquals, todayIso, accountDisplayName, apiErrorMessage, enumLabel,
} from '@/utils/ledger'

export default {
  name: 'TrialBalance',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormInput, BFormCheckbox, BTable, BAlert, BSpinner,
  },
  data() {
    const queryDate = this.$route && this.$route.query && this.$route.query.as_of
    const asOf = typeof queryDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(queryDate)
      ? queryDate
      : todayIso()
    return {
      loading: false,
      asOf,
      preClosing: false,
      accounts: [],
      totals: { debit: 0, credit: 0 },
    }
  },
  computed: {
    imbalanced() {
      if (!this.accounts.length && !this.totals.debit && !this.totals.credit) return false
      return !moneyEquals(this.totals.debit, this.totals.credit)
    },
    fields() {
      return [
        { key: 'code', label: this.$t('ledger_code') },
        { key: 'name', label: this.$t('ledger_name') },
        { key: 'debit', label: this.$t('ledger_debit') },
        { key: 'credit', label: this.$t('ledger_credit') },
        { key: 'movement_debit', label: this.$t('ledger_movement_debit') },
        { key: 'movement_credit', label: this.$t('ledger_movement_credit') },
      ]
    },
  },
  watch: {
    asOf: 'loadReport',
    preClosing: 'loadReport',
    '$route.query.as_of': function onAsOfQuery(value) {
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && value !== this.asOf) {
        this.asOf = value
      }
    },
  },
  mounted() {
    this.loadReport()
  },
  methods: {
    accountName(row) {
      return accountDisplayName(row, this.$i18n.locale) || enumLabel(row.type, this.$i18n.locale)
    },
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    async loadReport() {
      if (!this.asOf) return
      this.loading = true
      try {
        const params = { as_of: this.asOf }
        if (this.preClosing) params.pre_closing = 1
        const res = await getTrialBalance(params)
        const data = oneFrom(res)
        this.accounts = Array.isArray(data.accounts) ? data.accounts : []
        this.totals = data.totals || { debit: 0, credit: 0 }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-tb-card {
  border-bottom: 1px solid #eee;
  padding: 0.75rem 0.15rem;
}
.ledger-tb-totals {
  position: sticky;
  bottom: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: #fff;
  border-top: 2px solid #06284d;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
}
.ledger-sticky-head th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
</style>
